import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api from '../../api';
import { useNotification } from '../../contexts/NotificationContext';
import { useLanguage } from '../../contexts/LanguageContext';
import PageHeader from '../ui/PageHeader';
import Icon from '../ui/Icon';
import { fmtDateTime, fmtKamas, fmtKamasLong, fmtRemaining, toLocalInput, useServerClock } from './auctionUtils';
import './Encheres.css';

const DAY_MS = 24 * 60 * 60 * 1000;

/* ── Lancement d'une enchère ── */
const LaunchForm = ({ serverId, onLaunched }) => {
    const [searchParams] = useSearchParams();
    const { success, error: showError } = useNotification();
    const { t } = useLanguage();
    const [sessions, setSessions] = useState([]);
    const [channels, setChannels] = useState([]);
    const [form, setForm] = useState({
        sessionId: searchParams.get('session') || '',
        endsAt: toLocalInput(Date.now() + DAY_MS),
        startingBid: 50000,
        minIncrement: 50000,
        antiSnipeMinutes: 5,
        channelId: ''
    });
    const [launching, setLaunching] = useState(false);
    const setField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

    useEffect(() => {
        Promise.all([
            api.get(`/reservation-sessions/${serverId}`).catch(() => ({ data: [] })),
            api.get(`/reservation-config/${serverId}`).catch(() => ({ data: {} })),
            api.get(`/discord/channels/${serverId}`).catch(() => ({ data: [] }))
        ]).then(([sessionsRes, configRes, channelsRes]) => {
            const list = Array.isArray(sessionsRes.data) ? sessionsRes.data : [];
            setSessions(list);
            setChannels((channelsRes.data || []).filter(c => c.type === 0).sort((a, b) => a.name.localeCompare(b.name)));
            // Par défaut : la fiche en cours
            setForm(prev => (prev.sessionId ? prev : { ...prev, sessionId: configRes.data?.activeSessionId || list[0]?._id || '' }));
        });
    }, [serverId]);

    const setDuration = (days) => setField('endsAt', toLocalInput(Date.now() + days * DAY_MS));

    const launch = async (e) => {
        e.preventDefault();
        const session = sessions.find(s => s._id === form.sessionId);
        if (!window.confirm(t('auctionAdmin.confirmLaunch', 'Lancer les enchères sur les zones libres de « {title} » jusqu\'au {date} ?', { title: session?.title || t('auctionAdmin.theSheet', 'la fiche'), date: fmtDateTime(form.endsAt) }))) return;
        setLaunching(true);
        try {
            const res = await api.post(`/auctions/${serverId}`, { ...form, endsAt: new Date(form.endsAt).toISOString() });
            success(t('auctionAdmin.launched', 'Enchères lancées'));
            onLaunched(res.data.auction._id);
        } catch (err) {
            showError(err?.response?.data?.error || t('auctionAdmin.launchError', 'Impossible de lancer les enchères'));
        } finally {
            setLaunching(false);
        }
    };

    return (
        <section className="c2-card c2-section">
            <h2 className="c2-section__title"><Icon name="hammer" size={20} />{t('auctionAdmin.launchTitle', 'Lancer une enchère')}</h2>
            <p className="c2-section__desc">
                {t('auctionAdmin.launchDesc', 'Toutes les zones restées libres sur la fiche (hors zones BDA) sont mises aux enchères. Les gagnants récupèrent leur zone jusqu\'à la fin de la saison.')}
            </p>
            <form className="auc-launch" onSubmit={launch}>
                <div className="auc-launch__grid">
                    <label className="c2-field">
                        <span className="c2-field__label">{t('auctionAdmin.sheet', 'Fiche de réservation')}</span>
                        <select className="c2-select" value={form.sessionId} onChange={e => setField('sessionId', e.target.value)} required>
                            <option value="">{t('auctionAdmin.pickSheet', '— Choisir une fiche —')}</option>
                            {sessions.map(s => <option key={s._id} value={s._id}>{s.title}{s.frozen ? ` ${t('auctionAdmin.frozenSuffix', '(figée)')}` : ''}</option>)}
                        </select>
                    </label>
                    <label className="c2-field">
                        <span className="c2-field__label">{t('auctionAdmin.endsAt', 'Fin des enchères')}</span>
                        <input type="datetime-local" className="c2-input" value={form.endsAt} onChange={e => setField('endsAt', e.target.value)} required />
                        <span className="auc-launch__quick">
                            <button type="button" className="c2-btn c2-btn--ghost c2-btn--sm" onClick={() => setDuration(1)}>{t('auctionAdmin.oneDay', '1 jour')}</button>
                            <button type="button" className="c2-btn c2-btn--ghost c2-btn--sm" onClick={() => setDuration(2)}>{t('auctionAdmin.twoDays', '2 jours')}</button>
                        </span>
                    </label>
                    <label className="c2-field">
                        <span className="c2-field__label">{t('auctionAdmin.startingBid', 'Mise de départ (kamas)')}</span>
                        <input type="number" min="1" className="c2-input" value={form.startingBid} onChange={e => setField('startingBid', e.target.value)} />
                    </label>
                    <label className="c2-field">
                        <span className="c2-field__label">{t('auctionAdmin.minIncrement', 'Surenchère minimale (kamas)')}</span>
                        <input type="number" min="1" className="c2-input" value={form.minIncrement} onChange={e => setField('minIncrement', e.target.value)} />
                    </label>
                    <label className="c2-field">
                        <span className="c2-field__label">{t('auctionAdmin.antiSnipe', 'Anti-snipe (minutes)')}</span>
                        <input type="number" min="1" className="c2-input" value={form.antiSnipeMinutes} onChange={e => setField('antiSnipeMinutes', e.target.value)} />
                    </label>
                    <label className="c2-field">
                        <span className="c2-field__label">{t('auctionAdmin.channel', 'Salon Discord des enchères')}</span>
                        <select className="c2-select" value={form.channelId} onChange={e => setField('channelId', e.target.value)} required>
                            <option value="">{t('auctionAdmin.pickChannel', '— Choisir un salon —')}</option>
                            {channels.map(c => <option key={c.id} value={c.id}>#{c.name}</option>)}
                        </select>
                        <span className="c2-field__hint">{t('auctionAdmin.channelHint', 'Le bot y annonce l\'ouverture, tague le joueur dépassé à chaque surenchère et publie les résultats.')}</span>
                    </label>
                </div>
                <div className="c2-row">
                    <button type="submit" className="c2-btn c2-btn--primary" disabled={launching || !form.sessionId || !form.channelId}>
                        <Icon name="hammer" size={18} />
                        {launching ? t('auctionAdmin.launching', 'Lancement...') : t('auctionAdmin.launch', 'Lancer les enchères')}
                    </button>
                </div>
            </form>
        </section>
    );
};

/* ── Enchère en cours ── */
const CurrentAuction = ({ auction, serverId, now, onClosed }) => {
    const { success, error: showError } = useNotification();
    const { t } = useLanguage();
    const [closing, setClosing] = useState(false);
    const remaining = new Date(auction.lastEndsAt).getTime() - now;

    const close = async () => {
        const message = auction.running
            ? t('auctionAdmin.confirmCloseNow', 'Clôturer maintenant ? Les zones encore ouvertes se terminent immédiatement, les meilleures mises actuelles l\'emportent.')
            : t('auctionAdmin.confirmClose', 'Clôturer l\'enchère et annoncer les résultats ?');
        if (!window.confirm(message)) return;
        setClosing(true);
        try {
            await api.post(`/auctions/${serverId}/${auction._id}/close`);
            success(t('auctionAdmin.closed', 'Enchère clôturée'));
            onClosed();
        } catch (err) {
            showError(err?.response?.data?.error || t('auctionAdmin.closeError', 'Impossible de clôturer'));
        } finally {
            setClosing(false);
        }
    };

    return (
        <section className="c2-card c2-section auc-current">
            <div className="c2-section__head">
                <h2 className="c2-section__title"><Icon name="hammer" size={20} />{auction.title}</h2>
                {auction.running
                    ? <span className="c2-chip c2-chip--green">{t('auctionAdmin.running', 'En cours')} · {fmtRemaining(remaining)}</span>
                    : <span className="c2-chip c2-chip--purple">{t('auctionAdmin.toClose', 'Terminée · à clôturer')}</span>}
            </div>
            <div className="auc-kpis">
                <div className="c2-tile"><span>{t('auctionAdmin.zones', 'Zones')}</span><strong>{auction.stats.lotCount}</strong></div>
                <div className="c2-tile"><span>{t('auctionAdmin.zonesWithBid', 'Zones avec mise')}</span><strong>{auction.stats.soldCount}</strong></div>
                <div className="c2-tile"><span>{t('auctionAdmin.bids', 'Mises')}</span><strong>{auction.stats.bidCount}</strong></div>
                <div className="c2-tile auc-kpis__main"><span>{t('auctionAdmin.bidsTotal', 'Total des mises')}</span><strong>{fmtKamas(auction.stats.totalAmount)}</strong></div>
            </div>
            <p className="c2-muted auc-current__meta">
                {t('auctionAdmin.meta', 'Fin prévue le {date} · départ {start} · +{inc} minimum · anti-snipe {n} min', { date: fmtDateTime(auction.endsAt), start: fmtKamas(auction.startingBid), inc: fmtKamas(auction.minIncrement), n: auction.antiSnipeMinutes })}
            </p>
            <div className="c2-row">
                <Link to={`/server/${serverId}/encheres/${auction._id}`} className="c2-btn c2-btn--ghost">
                    {t('auctionAdmin.follow', 'Suivre les enchères')}
                    <Icon name="arrowRight" size={16} />
                </Link>
                <button type="button" className="c2-btn c2-btn--danger" onClick={close} disabled={closing}>
                    <Icon name="lock" size={18} />
                    {closing ? t('auctionAdmin.closing', 'Clôture...') : t('auctionAdmin.close', 'Clôturer')}
                </button>
            </div>
        </section>
    );
};

const EncheresPage = () => {
    const { serverId } = useParams();
    const navigate = useNavigate();
    const { t } = useLanguage();
    const { success, error: showError } = useNotification();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [deletingId, setDeletingId] = useState(null);
    const now = useServerClock(data?.now);

    const load = useCallback(() => {
        setLoading(true);
        api.get(`/auctions/${serverId}`)
            .then(res => setData(res.data))
            .catch(() => setData({ auctions: [], stats: null }))
            .finally(() => setLoading(false));
    }, [serverId]);

    useEffect(() => { load(); }, [load]);

    const deleteAuction = async (auction) => {
        if (!window.confirm(t('auctionAdmin.confirmDelete', 'Supprimer définitivement « {title} » de l\'historique ?\n\nSes zones, mises et paiements seront effacés et ne compteront plus dans les statistiques.', { title: auction.title }))) return;
        setDeletingId(auction._id);
        try {
            await api.delete(`/auctions/${serverId}/${auction._id}`);
            success(t('auctionAdmin.deleted', 'Enchère supprimée'));
            load();
        } catch (err) {
            showError(err?.response?.data?.error || t('auctionAdmin.deleteError', 'Impossible de supprimer l\'enchère'));
        } finally {
            setDeletingId(null);
        }
    };

    const current = data?.auctions.find(a => a.status === 'open');
    const history = useMemo(() => data?.auctions.filter(a => a.status === 'closed') || [], [data]);
    const stats = data?.stats;
    const unpaid = useMemo(() => history.filter(a => a.stats.paidCount < a.stats.soldCount), [history]);

    return (
        <div className="c2-scope c2-admin-page">
            <PageHeader
                icon="hammer"
                title={t('navigation.auctions', 'Enchères')}
                subtitle={t('auctionAdmin.subtitle', 'Zones libres mises aux enchères après la phase d\'échange')}
                actions={
                    <button type="button" className="c2-btn c2-btn--ghost" onClick={load} disabled={loading}>
                        <Icon name="refresh" size={18} />
                        {t('common.refresh', 'Actualiser')}
                    </button>
                }
            />

            {loading && !data ? (
                <div className="c2-loading"><div className="c2-spinner" /><p>{t('common.loading', 'Chargement...')}</p></div>
            ) : (
                <>
                    {current
                        ? <CurrentAuction auction={current} serverId={serverId} now={now} onClosed={load} />
                        : <LaunchForm serverId={serverId} onLaunched={(id) => navigate(`/server/${serverId}/encheres/${id}`)} />}

                    {unpaid.length > 0 && (
                        <section className="c2-card c2-section">
                            <h2 className="c2-section__title"><Icon name="gem" size={20} />{t('auctionAdmin.paymentsToTrack', 'Paiements à suivre')}</h2>
                            <ul className="auc-unpaid">
                                {unpaid.map(a => (
                                    <li key={a._id}>
                                        <Link to={`/server/${serverId}/encheres/${a._id}`} className="c2-tile auc-unpaid__item">
                                            <span><strong>{a.title}</strong><span className="c2-muted">{fmtDateTime(a.closedAt || a.endsAt)}</span></span>
                                            <span className="c2-chip c2-chip--purple">{t('auctionAdmin.unpaidZones', '{n} zone(s) non payée(s)', { n: a.stats.soldCount - a.stats.paidCount })}</span>
                                            <Icon name="arrowRight" size={16} />
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}

                    {stats && stats.auctionCount > 0 && (
                        <section className="c2-card c2-section">
                            <h2 className="c2-section__title"><Icon name="chart" size={20} />{t('auctionAdmin.statistics', 'Statistiques')}</h2>
                            <div className="auc-kpis">
                                <div className="c2-tile"><span>{t('auctionAdmin.auctions', 'Enchères')}</span><strong>{stats.auctionCount}</strong></div>
                                <div className="c2-tile"><span>{t('auctionAdmin.zonesSold', 'Zones vendues')}</span><strong>{stats.soldCount}</strong></div>
                                <div className="c2-tile"><span>{t('auctionAdmin.avgPrice', 'Prix moyen')}</span><strong>{fmtKamas(stats.averageAmount)}</strong></div>
                                <div className="c2-tile auc-kpis__main"><span>{t('auctionAdmin.totalSold', 'Total vendu')}</span><strong>{fmtKamas(stats.totalAmount)}</strong></div>
                                <div className="c2-tile"><span>{t('auctionAdmin.leftToPay', 'Reste à payer')}</span><strong>{fmtKamas(stats.unpaidAmount)}</strong></div>
                            </div>
                            <div className="auc-tops">
                                <div className="c2-tile auc-top">
                                    <h3>{t('auctionAdmin.topZones', 'Zones les plus chères')}</h3>
                                    {stats.topZones.length === 0 ? <p className="c2-muted">—</p> : (
                                        <ol>{stats.topZones.map(z => <li key={z.zone}><span>{z.zone}</span><strong>{fmtKamasLong(z.max)}</strong></li>)}</ol>
                                    )}
                                </div>
                                <div className="c2-tile auc-top">
                                    <h3>{t('auctionAdmin.topBuyers', 'Plus gros acheteurs')}</h3>
                                    {stats.topBuyers.length === 0 ? <p className="c2-muted">—</p> : (
                                        <ol>{stats.topBuyers.map(b => <li key={b.bidderId}><span>{b.bidderName} · {b.count > 1 ? t('auctionAdmin.zonesN', '{n} zones', { n: b.count }) : t('auctionAdmin.zoneN', '{n} zone', { n: b.count })}</span><strong>{fmtKamasLong(b.total)}</strong></li>)}</ol>
                                    )}
                                </div>
                            </div>
                        </section>
                    )}

                    <section className="c2-card c2-section">
                        <h2 className="c2-section__title"><Icon name="history" size={20} />{t('auctionAdmin.history', 'Historique')}</h2>
                        {history.length === 0 ? (
                            <p className="c2-muted">{t('auctionAdmin.noHistory', 'Aucune enchère terminée pour le moment.')}</p>
                        ) : (
                            <div className="c2-table-wrap">
                                <table className="c2-table">
                                    <thead>
                                        <tr>
                                            <th>{t('auctionAdmin.auction', 'Enchère')}</th>
                                            <th>{t('auctionAdmin.closedOn', 'Clôturée le')}</th>
                                            <th className="is-center">{t('auctionAdmin.zonesSold', 'Zones vendues')}</th>
                                            <th className="is-num">{t('auctionAdmin.total', 'Total')}</th>
                                            <th className="is-center">{t('auctionAdmin.paid', 'Payées')}</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {history.map(a => (
                                            <tr key={a._id}>
                                                <td><strong>{a.title}</strong></td>
                                                <td className="c2-muted">{fmtDateTime(a.closedAt)}</td>
                                                <td className="is-center">{a.stats.soldCount}/{a.stats.lotCount}</td>
                                                <td className="is-num">{fmtKamas(a.stats.totalAmount)}</td>
                                                <td className="is-center">
                                                    <span className={`c2-chip${a.stats.paidCount === a.stats.soldCount ? ' c2-chip--green' : ''}`}>{a.stats.paidCount}/{a.stats.soldCount}</span>
                                                </td>
                                                <td className="is-num auc-history__actions">
                                                    <Link to={`/server/${serverId}/encheres/${a._id}`} className="c2-btn c2-btn--ghost c2-btn--sm">{t('auctionAdmin.detail', 'Détail')}</Link>
                                                    <button
                                                        type="button"
                                                        className="c2-icon-btn c2-icon-btn--sm c2-icon-btn--danger"
                                                        onClick={() => deleteAuction(a)}
                                                        disabled={deletingId === a._id}
                                                        aria-label={t('auctionAdmin.deleteAuction', 'Supprimer {title}', { title: a.title })}
                                                        title={t('auctionAdmin.deleteTitle', 'Supprimer de l\'historique')}
                                                    >
                                                        <Icon name="trash" size={16} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </section>
                </>
            )}
        </div>
    );
};

export default EncheresPage;
